<template>
  <div>

    <b-row>
      <b-col>
        <h2>{{ trans('financial_reports') }}</h2>
      </b-col>
      <b-col col-auto class="text-right">
        <date-range-picker
          ref="picker"
          v-model="dateRange"
          :ranges="defaultRanges"
          opens="left"
          @update="onChangeDateRange"
          class="mb-2"
        >
          <div slot="input" style="min-width: 350px;">
            <i class="fa fa-fw fa-calendar"></i>
            {{ moment(dateRange.startDate).format('LL') }} - {{ moment(dateRange.endDate).format('LL') }}
            <i class="fa fa-fw fa-caret-down"></i>
          </div>
        </date-range-picker>
        <amilia-financial-links
          :slug="amilia_organization.slug"
          :start="start"
          :end="end"
          class="ml-2 mb-2"
        />
      </b-col>
    </b-row>

    <div class="alert alert-danger" v-if="!!error">{{ error }}</div>

    <b-tabs lazy v-model="tabIndex" @input="onChangeTab" class="mt-4">

      <b-tab :title="trans('payments')">
        <b-row>
          <b-col>
            <p class="lead mt-4 mb-2">
              {{ trans('payments_summary') }}
            </p>
            <b-table
              ref="paymentsSummary"
              show-empty
              small
              bordered
              responsive
              :items="paymentsSummaryItems"
              :fields="paymentsSummaryFields"
              :busy="paymentsSummaryBusy"
            >
              <template v-slot:empty="scope">
                <p v-if="!paymentsSummaryBusy" class="text-center">
                  {{ trans('no_payments') }}
                </p>
                <p v-if="paymentsSummaryBusy" class="text-center text-muted my-2">
                  <b-spinner class="align-middle"></b-spinner>
                  <strong class="ml-2">{{ trans('loading') }}</strong>
                </p>
              </template>
            </b-table>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <p class="lead mt-4 mb-2">
              {{ trans('payments') }}:
              {{ paymentsTotalRows }} {{ paymentsTotalRows <= 1 ? trans('row').toLowerCase() : trans('rows').toLowerCase() }}
            </p>
            <b-table
              ref="payments"
              show-empty
              small
              bordered
              responsive
              :items="paymentsProvider"
              :fields="paymentFields"
              :per-page="paymentsPerPage"
              :current-page="paymentsCurrentPage"
              :busy="paymentsBusy"
              @refreshed="getPaymentsSummary"
            >
              <template v-slot:empty="scope">
                <p v-if="!paymentsBusy" class="text-center">
                  {{ trans('no_payments') }}
                </p>
                <p v-if="paymentsBusy" class="text-center text-muted my-2">
                  <b-spinner class="align-middle"></b-spinner>
                  <strong class="ml-2">{{ trans('loading') }}</strong>
                </p>
              </template>
            </b-table>
            <b-pagination
              :per-page="paymentsPerPage"
              :current-page="paymentsCurrentPage"
              :total-rows="paymentsTotalRows"
              v-model="paymentsCurrentPage"
              aria-controls="payments"
              align="center"
            />
          </b-col>
        </b-row>
      </b-tab>

      <b-tab :title="trans('sales')">
        <b-row>
          <b-col>
            <p class="lead mt-4 mb-2">
              {{ trans('sales_summary') }}
            </p>
            <b-table
              ref="salesSummary"
              show-empty
              small
              bordered
              responsive
              :items="salesSummaryItems"
              :fields="salesSummaryFields"
              :busy="salesSummaryBusy"
            >
              <template v-slot:empty="scope">
                <p v-if="!salesSummaryBusy" class="text-center">
                  {{ trans('no_invoice_items') }}
                </p>
                <p v-if="salesSummaryBusy" class="text-center text-muted my-2">
                  <b-spinner class="align-middle"></b-spinner>
                  <strong class="ml-2">{{ trans('loading') }}</strong>
                </p>
              </template>
            </b-table>

          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <b-row class="mt-4 mb-2">
              <b-col>
                <div class="lead">
                  <span>{{ trans('invoice_items') }}:</span>

                  <b-dropdown
                    :text="`${trans('type')}: ${trans(invoiceItemType || 'all')}`"
                    :variant="invoiceItemType ? 'outline-danger' : 'outline-secondary'"
                    size="sm"
                    class="mx-2"
                  >
                    <b-dropdown-item href="#" @click.prevent="invoiceItemType = null; saveSettings(); filterInvoiceItems()">
                      <i :class="`fa fa-fw ${invoiceItemType == null ? 'fa-check' : ''}`"></i>
                      {{ trans('all') }}
                      <span>({{ allInvoiceItems ? allInvoiceItems.length : 0 }})</span>
                    </b-dropdown-item>
                    <b-dropdown-item
                      v-for="item in invoiceItemTypes"
                      v-bind:key="item.value"
                      href="#"
                      @click.prevent="invoiceItemType = item.value; saveSettings(); filterInvoiceItems()"
                    >
                      <i :class="`fa fa-fw ${item.value == invoiceItemType ? 'fa-check' : ''}`"></i>
                      {{ item.label }}
                      <span v-if="item.count">({{ item.count }})</span>
                    </b-dropdown-item>
                  </b-dropdown>

                  <small>
                    {{ invoiceItems ? invoiceItems.length : 0 }} {{ invoiceItems && invoiceItems.length <= 1 ? trans('row').toLowerCase() : trans('rows').toLowerCase() }}
                  </small>
                </div>
              </b-col>
              <b-col class="col-auto text-right">
                <div class="lead">
                  <a :href="downloadSalesUrl" class="btn btn-sm btn-outline-success ml-2">
                    <i class="fa fa-fw fa-file-excel"></i>
                  </a>
                </div>
              </b-col>
            </b-row>
            <b-table
              ref="invoiceItems"
              show-empty
              small
              bordered
              responsive
              :items="invoiceItems"
              :fields="invoiceItemFields"
              :per-page="invoiceItemsPerPage"
              :current-page="invoiceItemsCurrentPage"
            >
              <template v-slot:empty="scope">
                <p v-if="!invoiceItemsBusy" class="text-center">
                  {{ trans('no_invoice_items') }}
                </p>
                <p v-if="invoiceItemsBusy" class="text-center text-muted my-2">
                  <b-spinner class="align-middle"></b-spinner>
                  <strong class="ml-2">{{ trans('loading') }}</strong>
                </p>
              </template>
              <template v-slot:cell(invoice_item_id)="data">
                {{ data.value }}
                <a :href="data.item.invoice_url" @click.prevent="openPopupWindow(data.item.invoice_url)">
                  <i class="fa fa-fw fa-file-invoice-dollar"></i>
                </a>
                <a :href="`/organization/${organization.id}/amilia/invoice_items/${data.item.invoice_item_id}`" target="_blank" v-if="user.id == 1" class="text-danger">
                  <i class="fa fa-fw fa-file-code"></i>
                </a>
              </template>
              <template v-slot:cell(description)="data">
                <a v-if="data.value && data.value.length > 20" href="#" @click.prevent="onClickDescription(data.item.invoice_item_id)">[{{ data.item.showDescription ? '-' : '+' }}]</a>
                <span v-if="data.item.showDescription" v-html="data.value"></span>
                <span v-if="data.value && data.value.length > 20 && !data.item.showDescription">{{ data.value.substr(0, 20) }}...</span>
              </template>
            </b-table>
            <b-pagination
              :per-page="invoiceItemsPerPage"
              :total-rows="invoiceItems ? invoiceItems.length : 0"
              v-model="invoiceItemsCurrentPage"
              aria-controls="invoiceItems"
              align="center"
            />
          </b-col>
        </b-row>
      </b-tab>

    </b-tabs>

  </div>
</template>

<script>
  import queryString from 'query-string'
  import DateRangePicker from 'vue2-daterange-picker'
  import AmiliaFinancialLinks from './AmiliaFinancialLinksComponent'

  let defaultRanges = {}
  defaultRanges[trans('today')] = [moment(), moment()]
  defaultRanges[trans('yesterday')] = [moment().subtract(1, 'days'), moment().subtract(1, 'days')]
  defaultRanges[trans('this_week')] = [moment().startOf('week'), moment().endOf('week')]
  defaultRanges[trans('last_week')] = [moment().subtract(1, 'week').startOf('week'), moment().subtract(1, 'week').endOf('week')]
  defaultRanges[trans('this_month')] = [moment().startOf('month'), moment().endOf('month')]
  defaultRanges[trans('last_month')] = [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')]

  function currencyFormatterFn(value, key, item) {
    return numeral(value).format('0,0.00')
  }

  export default {
    components: {
      DateRangePicker,
      AmiliaFinancialLinks
    },
    props: [
      'user',
      'organization',
      'amilia_organization',
    ],
    data() {
      return {
        trans: trans,
        numeral: numeral,
        moment: moment,

        error: null,
        dateRange: {
          startDate: null,
          endDate: null,
        },
        defaultRanges: defaultRanges,
        tabIndex: 0,

        // Payments
        paymentFields: [
          {key: 'payment_id', label: 'ID'},
          {
            key: 'transaction_date',
            label: trans('date'),
            formatter: (value, key, item) => {
              return moment(value).format('LL')
            },
          },
          {
            key: 'amount',
            label: trans('amount') + ' $',
            formatter: currencyFormatterFn,
          },
        ],
        paymentsBusy: false,
        payments: null,
        paymentsPerPage: 10,
        paymentsCurrentPage: 1,
        paymentsTotalRows: 0,
        paymentsSummaryBusy: true,
        paymentsSummaryItems: [],
        paymentsSummaryStart: null,
        paymentsSummaryEnd: null,
        paymentsSummaryFields: [
          {
            key: 'type',
            label: trans('type'),
            formatter: (value, key, item) => {
              if (value == 'CC')
                return trans('credit_card')
              if (value == 'DD')
                return trans('direct debit')
              if (value.indexOf('OP.') === 0)
                return trans('offline') + ' ' + value.substr(3).replace(/([a-z])([A-Z])/g, '$1 $2')
              return value
            },
          },
          {
            key: 'payment',
            label: trans('payment') + ' $',
            formatter: currencyFormatterFn,
          },
          {
            key: 'refund',
            label: trans('refund') + ' $',
            formatter: currencyFormatterFn,
          },
          {
            key: 'net',
            label: trans('net') + ' $',
            formatter: currencyFormatterFn,
          },
        ],

        // Sales
        invoiceItemFields: [
          {key: 'invoice_item_id', label: 'ID'},
          {
            key: 'date',
            label: trans('date'),
            formatter: (value, key, item) => {
              return moment(value).format('LLL')
            },
          },
          {
            key: 'type',
            label: trans('type'),
            formatter: (value, key, item) => {
              return trans(value)
            },
          },
          {key: 'description', label: trans('description')},
          {key: 'ledger', label: trans('ledger')},
          {key: 'program', label: trans('program')},
          {key: 'location', label: trans('location')},
          {key: 'city', label: trans('city')},
          {
            key: 'amount',
            label: trans('sales') + ' $',
            formatter: currencyFormatterFn,
          },
          {
            key: 'discount',
            label: trans('discounts') + ' $',
            formatter: currencyFormatterFn,
          },
          {
            key: 'cancellation',
            label: trans('cancellations') + ' $',
            formatter: currencyFormatterFn,
          },
          {
            key: 'net_sale',
            label: trans('net') + ' $',
            formatter: currencyFormatterFn,
          },
          {
            key: 'tax1',
            label: trans('tax') + '1 $',
            formatter: currencyFormatterFn,
          },
          {
            key: 'tax2',
            label: trans('tax') + '2 $',
            formatter: currencyFormatterFn,
          },
          {
            key: 'tax3',
            label: trans('tax') + '3 $',
            formatter: currencyFormatterFn,
          },
          {
            key: 'total',
            label: trans('total') + ' $',
            formatter: currencyFormatterFn,
          },
        ],
        invoiceItemsBusy: false,
        invoiceItems: null,
        allInvoiceItems: null,
        invoiceItemsPerPage: 10,
        invoiceItemsCurrentPage: 1,
        invoiceItemTypes: _.map(['AII', 'CRDII', 'CTII', 'MII', 'PeII', 'PRDII', 'PPI', 'RGTII', 'SFII', 'P2II', 'F2II', 'DONII', 'SEGII', 'MPII', 'SHIPII', 'SUBII', 'RBII', 'REII', 'IBII', 'FFII', 'FDII', 'PLII'], type => {
          return {
            value: type,
            label: trans(type),
            count: 0,
          }
        }),
        invoiceItemType: null,
        salesSummaryBusy: true,
        salesSummaryItems: [],
        salesSummaryStart: null,
        salesSummaryEnd: null,
        salesSummaryFields: [
          {
            key: 'type',
            label: trans('type'),
            formatter: (value, key, item) => {
              return trans(value)
            },
          },
          {
            key: 'amount',
            label: trans('sales') + ' $',
            formatter: currencyFormatterFn,
          },
          {
            key: 'discount',
            label: trans('discounts') + ' $',
            formatter: currencyFormatterFn,
          },
          {
            key: 'cancellation',
            label: trans('cancellations') + ' $',
            formatter: currencyFormatterFn,
          },
          {
            key: 'net_sale',
            label: trans('net') + ' $',
            formatter: currencyFormatterFn,
          },
          {
            key: 'tax1',
            label: trans('tax') + '1 $',
            formatter: currencyFormatterFn,
          },
          {
            key: 'tax2',
            label: trans('tax') + '2 $',
            formatter: currencyFormatterFn,
          },
          {
            key: 'tax3',
            label: trans('tax') + '3 $',
            formatter: currencyFormatterFn,
          },
          {
            key: 'total',
            label: trans('total') + ' $',
            formatter: currencyFormatterFn,
          },
        ],
      }
    },
    computed: {
      start() {
        return moment(this.dateRange.startDate).format('YYYY-MM-DD')
      },
      end() {
        return moment(this.dateRange.endDate).format('YYYY-MM-DD')
      },
      downloadSalesUrl() {
        return `/organization/${this.organization.id}/amilia/download_sales?start=${this.start}&end=${this.end}`
      },
    },
    methods: {
      openPopupWindow(url) {
        var popupWnd = window.open(url, 'info', 'toolbar=0,scrollbars=1,location=0,statusbar=0,menubar=0,resizable=1,width=700,height=500');
        if (window.focus) popupWnd.focus();
      },
      onClickDescription(id) {
        const index = _.findIndex(this.invoiceItems, {invoice_item_id: id})
        this.invoiceItems[index].showDescription = !this.invoiceItems[index].showDescription
        this.invoiceItems.splice(index, 1, this.invoiceItems[index])
      },
      onChangeTab() {
        this.saveSettings()
      },
      onChangeDateRange() {
        this.saveSettings()
        if (this.tabIndex == 0) this.$refs.payments.refresh()
        if (this.tabIndex == 1) this.loadInvoiceItems()
      },
      loadSettings() {
        // Try query string first and fallback to local storage
        const settings = _.extend({
            tabIndex: 0,
            start: moment().startOf('month').format('YYYY-MM-DD'),
            end: moment().endOf('month').format('YYYY-MM-DD'),
            iiType: null,
          },
          _.getObjectFromLocalStorage('transactionsReportSettings'),
          _.pick(queryString.parse(location.search), ['tabIndex', 'start', 'end', 'iiType']),
        );
        this.tabIndex = parseInt(settings.tabIndex, 10)
        this.dateRange.startDate = settings.start
        this.dateRange.endDate = settings.end
        this.invoiceItemType = settings.iiType
        this.saveSettings();

        if (this.tabIndex == 1) this.loadInvoiceItems()
      },
      saveSettings() {
        const settings = {
          tabIndex: this.tabIndex,
          start: moment(this.dateRange.startDate).format('YYYY-MM-DD'),
          end: moment(this.dateRange.endDate).format('YYYY-MM-DD'),
          iiType: this.invoiceItemType,
        }

        // Replace query string
        history.replaceState({}, null, window.location.protocol + "//" + window.location.host + window.location.pathname + '?' + queryString.stringify(settings))

        // Save to local storage
        window.localStorage['transactionsReportSettings'] = JSON.stringify(settings)
      },
      getPaymentsSummary() {
        if (this.start == this.paymentsSummaryStart && this.end == this.paymentsSummaryEnd) {
          this.paymentsSummaryBusy = false
          return;
        }
        const url = `/organization/${this.organization.id}/amilia/payments_summary?start=${this.start}&end=${this.end}`

        this.paymentsSummaryBusy = true
        this.error = null
        this.paymentsSummaryItems = []

        return axios.get(url)
        .then(response => {
          this.paymentsSummaryBusy = false
          this.paymentsSummaryStart = this.start
          this.paymentsSummaryEnd = this.end
          this.paymentsSummaryItems = response.data
        })
        .catch(error => {
          this.paymentsSummaryBusy = false
          if (error.response.data) {
            this.error = error.response.data.errors ? _.values(error.response.data.errors).join(' ') : error.response.data.message
          } else {
            this.error = error
          }
          if (!this.error) this.error = trans('error')
        })
      },
      paymentsProvider() {
        const url = `/organization/${this.organization.id}/amilia/payments?start=${this.start}&end=${this.end}&page=${this.paymentsCurrentPage}&per_page=${this.paymentsPerPage}`

        this.paymentsBusy = true
        this.paymentsSummaryBusy = true
        this.error = null

        return axios.get(url)
        .then(response => {
          this.paymentsBusy = false
          this.paymentsCurrentPage = response.data.current_page
          this.paymentsPerPage = response.data.per_page
          this.paymentsTotalRows = response.data.total
          this.payments = response.data.data.map(o => {
            o.amount = parseFloat(o.amount, 10)
            return o
          })
          return this.payments
        })
        .catch(error => {
          this.paymentsBusy = false
          if (error.response.data) {
            this.error = error.response.data.errors ? _.values(error.response.data.errors).join(' ') : error.response.data.message
          } else {
            this.error = error
          }
          if (!this.error) this.error = trans('error')
          this.payments = []
          return this.payments
        })
      },
      getSalesSummary() {
        if (this.start == this.salesSummaryStart && this.end == this.salesSummaryEnd) {
          this.salesSummaryBusy = false
          return;
        }

        const url = `/organization/${this.organization.id}/amilia/sales_summary?start=${this.start}&end=${this.end}`

        this.salesSummaryBusy = true
        this.error = null
        this.salesSummaryItems = []

        return axios.get(url)
        .then(response => {
          this.salesSummaryBusy = false
          this.salesSummaryStart = this.start
          this.salesSummaryEnd = this.end
          this.salesSummaryItems = response.data
        })
        .catch(error => {
          this.salesSummaryBusy = false
          if (error.response.data) {
            this.error = error.response.data.errors ? _.values(error.response.data.errors).join(' ') : error.response.data.message
          } else {
            this.error = error
          }
          if (!this.error) this.error = trans('error')
        })
      },
      loadInvoiceItems() {
        const url = `/organization/${this.organization.id}/amilia/sales?start=${this.start}&end=${this.end}`
        
        this.invoiceItemsBusy = true
        this.salesSummaryBusy = true
        this.error = null
        let typeCounts = {}

        return axios.get(url)
        .then(response => {
          this.invoiceItemsBusy = false
          this.allInvoiceItems = response.data.map(o => {
            if (typeCounts[o.type] === undefined) typeCounts[o.type] = 0
            typeCounts[o.type] += 1
            o.amount = parseFloat(o.amount, 10)
            o.tax1 = parseFloat(o.tax1, 10)
            o.tax2 = parseFloat(o.tax2, 10)
            o.tax3 = parseFloat(o.tax3, 10)
            o.net_sale = parseFloat(o.net_sale, 10)
            o.total = parseFloat(o.total, 10)
            o.invoice_url = `https://www.amilia.com/Clients/en/${this.organization.amilia_organization.slug}/Invoice/${o.invoice_id}`
            return o
          })

          this.invoiceItemTypes.forEach(o => {
            o.count = typeCounts[o.value] || 0
          })

          this.filterInvoiceItems()
          this.getSalesSummary()
        })
        .catch(error => {
          this.invoiceItemsBusy = false
          if (error.response.data) {
            this.error = error.response.data.errors ? _.values(error.response.data.errors).join(' ') : error.response.data.message
          } else {
            this.error = error
          }
          if (!this.error) this.error = trans('error')
          this.allInvoiceItems = []
          this.filterInvoiceItems()
          this.getSalesSummary()
        })
      },
      filterInvoiceItems() {
        this.invoiceItemsCurrentPage = 1
        this.invoiceItems = this.allInvoiceItems.reduce((l, o) => {
          if (this.invoiceItemType == null || o.type == this.invoiceItemType) {
            l.push(o)
          }
          return l
        }, [])
      }
    },
    mounted() {
      window.co = this
      this.loadSettings()
    }
  }
</script>
